import type { CreativeBriefResponse } from '@innovationdepartment/proxima-sdk-axios';
import { useToaster } from '@innovationdepartment/proxima-ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import CreativeBriefsView from './CreativeBriefs.View';
import useCBFilterState from './useCBFilterState';
import useCreativeBriefsApi from './useCreativeBriefsApi';

const CreativeBriefsContainer = () => {
  const { filterState, filterOptions, briefs, setBriefs, total, loading, error, handlers } =
    useCBFilterState();
  const { updateBriefVisibility } = useCreativeBriefsApi();
  const [updatingBriefIds, setUpdatingBriefIds] = useState<Set<string>>(new Set());
  const [selectedBrief, setSelectedBrief] = useState<CreativeBriefResponse | null>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const { showToaster } = useToaster();
  const showToasterRef = useRef(showToaster);

  useEffect(() => {
    if (error) showToasterRef.current({ message: error, variant: 'error' });
  }, [error]);

  const handleToggleVisibility = useCallback(
    async (briefId: string, visibleToCustomer: boolean) => {
      setUpdatingBriefIds(prev => new Set(prev).add(briefId));

      const result = await updateBriefVisibility(briefId, visibleToCustomer);

      if (result.success) {
        setBriefs(prev => prev.map(b => (b.briefId === briefId ? { ...b, visibleToCustomer } : b)));
      } else {
        showToasterRef.current({ message: result.error, variant: 'error' });
      }

      setUpdatingBriefIds(prev => {
        const next = new Set(prev);
        next.delete(briefId);
        return next;
      });
    },
    [updateBriefVisibility, setBriefs]
  );

  const handleBriefClick = useCallback((brief: CreativeBriefResponse) => {
    setSelectedBrief(brief);
    setIsPanelOpen(true);
  }, []);

  const handlePanelClose = useCallback(() => {
    setIsPanelOpen(false);
    setSelectedBrief(null);
  }, []);

  return (
    <CreativeBriefsView
      filterState={filterState}
      filterOptions={filterOptions}
      handlers={handlers}
      briefs={briefs}
      total={total}
      loading={loading}
      onToggleVisibility={handleToggleVisibility}
      updatingBriefIds={updatingBriefIds}
      selectedBrief={selectedBrief}
      isPanelOpen={isPanelOpen}
      onPanelClose={handlePanelClose}
      onBriefClick={handleBriefClick}
    />
  );
};

export default CreativeBriefsContainer;
